import type { ComponentPropsWithRef, ReactNode } from 'react';

import { Switch } from '@base-ui/react/switch';

import { cn } from '../../../lib/cn';
import { Spinner } from '../../core/Spinner';
import { ChoiceField } from './ChoiceField';
import { useFieldIds } from './useFieldIds';

export type ToggleSize = 'sm' | 'md';

export interface ToggleProps extends Omit<ComponentPropsWithRef<'span'>, 'children' | 'onChange' | 'checked'> {
  /** Controlled state. Omit for an uncontrolled switch — `defaultChecked` still passes through. */
  checked?: boolean;
  /**
   * Base UI hands this the change event details as a second argument; a handler
   * that only wants the boolean is assignable to it.
   */
  onCheckedChange?: (checked: boolean) => void;
  label: ReactNode;
  /** Swaps the knob for a spinner, sets `aria-busy` and blocks interaction. */
  loading?: boolean;
  error?: string | null;
  success?: string | null;
  hint?: string | null;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  size?: ToggleSize;
}

/**
 * Track and travel are derived from the same numbers that decide the track box:
 * `p-0.5` (2px) plus a 2px border on each side leaves the thumb 16px / 12px of
 * room to travel, which is exactly `translate-x-5` / `translate-x-4`.
 */
const SIZES: Record<ToggleSize, { track: string; thumb: string }> = {
  sm: { track: 'h-5 w-9', thumb: 'size-3 data-checked:translate-x-4' },
  md: { track: 'h-6 w-11', thumb: 'size-4 data-checked:translate-x-5' },
};

/**
 * Switch.
 *
 * Base UI's `Switch.Root` renders a `<span role="switch" aria-checked>` plus a
 * hidden `<input type="checkbox">` beside it, so keyboard activation, form
 * participation and the `role="switch"` + `aria-checked` pair the spec requires
 * are all already correct in the installed version — nothing is re-built here.
 * Base UI also names the span from the `ChoiceField` label through its own sibling
 * fallback, so the visible label and the announced name are the same text.
 */
export function Toggle({
  checked,
  onCheckedChange,
  label,
  loading = false,
  error,
  success,
  hint,
  required = false,
  disabled = false,
  size = 'md',
  id: providedId,
  className,
  ...rest
}: ToggleProps) {
  const fieldIds = useFieldIds(providedId, { error, success, hint });
  const blocked = disabled || loading;

  return (
    <ChoiceField
      id={fieldIds.id}
      label={label}
      required={required}
      disabled={blocked}
      error={error}
      success={success}
      hint={hint}
      inline
      className={className}
    >
      <Switch.Root
        {...rest}
        id={fieldIds.id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={blocked}
        required={required}
        aria-busy={loading || undefined}
        aria-invalid={fieldIds.invalid || undefined}
        aria-describedby={fieldIds.describedBy}
        className={cn(
          // 24px tall at `md`, so the class carries the 44px overlay too.
          'dhd-target relative inline-flex shrink-0 cursor-pointer items-center rounded-pill border-2 p-0.5',
          'transition-colors duration-[var(--dur-base)] ease-[var(--ease-spring)]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
          'data-unchecked:border-line-strong data-unchecked:bg-neutral',
          'data-checked:border-primary data-checked:bg-primary',
          'data-disabled:cursor-not-allowed',
          disabled && 'opacity-45',
          SIZES[size].track,
        )}
      >
        {loading ? (
          // Centred by its own wrapper: the spinner's rotation is a transform, and
          // a translate on the same element would be overwritten by the keyframes.
          <span className="pointer-events-none absolute inset-0 grid place-items-center">
            <Spinner size="sm" />
          </span>
        ) : (
          <Switch.Thumb
            className={cn(
              'block shrink-0 rounded-pill bg-inverse',
              'transition-transform duration-[var(--dur-base)] ease-[var(--ease-spring)]',
              SIZES[size].thumb,
            )}
          />
        )}
      </Switch.Root>
    </ChoiceField>
  );
}
